import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import useTableSearch from "../../../hooks/useTableSearch";

import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
} from "../../common/adminTable";
import { buildPaginationMeta } from "../../../utils/clientPagination";
import { formatListingDateTime } from "../../../utils/formatListingDate";
import { listSupplierOptions, listTransactions } from "./service";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";

const Transactions = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();

  const [showFilters, setShowFilters] = useState(false);
  const [status, setStatus] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const filterRef = useRef(null);

  const [showToaster, setShowToaster] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterType, setToasterType] = useState("success");

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowFilters(false);
      }
    };
    if (showFilters) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilters]);

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "admin-transactions",
      currentPage,
      activeSearch,
      status,
      serviceType,
      supplierId,
    ],
    queryFn: () =>
      listTransactions(currentPage, activeSearch, {
        status,
        service_type: serviceType,
        supplier_id: supplierId,
        per_page: 10,
      }),
    keepPreviousData: true,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const { data: supplierOptions = [] } = useQuery({
    queryKey: ["admin-suppliers-options"],
    queryFn: () => listSupplierOptions(""),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error) {
      setToasterMessage(error.message || "Failed to load transactions");
      setToasterType("error");
      setShowToaster(true);
    }
  }, [error]);

  const transactions = response?.data || [];
  const current_page = response?.current_page || 1;
  const last_page = response?.last_page || 1;
  const total = response?.total || 0;
  const per_page = response?.per_page || 10;

  const pagination = buildPaginationMeta({
    currentPage: current_page,
    lastPage: last_page,
    perPage: per_page,
    total,
  });

  const handlePageChange = (page) => {
    if (page !== currentPage) setCurrentPage(page);
  };

  const clearFilters = useCallback(() => {
    clearSearch();
    setStatus("");
    setServiceType("");
    setSupplierId("");
    setCurrentPage(1);
  }, [clearSearch]);

  const formatAmount = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatServiceType = (t) => {
    if (!t) return "—";
    return t.charAt(0).toUpperCase() + t.slice(1);
  };

  const renderStatusBadge = (s) => {
    const val = String(s || "").toLowerCase();
    if (val === "completed" || val === "success") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Completed
        </span>
      );
    }
    if (val === "failed") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Failed
        </span>
      );
    }
    if (val === "refunded") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Refunded
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        Pending
      </span>
    );
  };

  const pageMeta = ADMIN_PAGE_META.transactions;

  return (
    <AdminPageLayout>
      <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <TableSearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search transactions..."
              className="sm:w-[200px]"
            />
            <div className="flex items-center gap-2">
              <div className="relative" ref={filterRef}>
                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  className="bg-[#221F37] text-white px-4 py-1 rounded-lg border shadow-md transition-all text-sm font-medium flex items-center gap-1"
                >
                  Filter{" "}
                  <i className="fi fi-rr-settings-sliders text-sm flex items-center justify-center"></i>
                </button>
                {showFilters && (
                  <div className="absolute right-0 mt-2 z-30 w-64 rounded-lg border border-gray-200 bg-white shadow-lg p-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Service
                    </label>
                    <select
                      value={serviceType}
                      onChange={(e) => {
                        setServiceType(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm text-gray-800 focus:outline-none focus:border-primary mb-3"
                    >
                      <option value="">All services</option>
                      <option value="package">Package</option>
                      <option value="event">Event</option>
                      <option value="attraction">Attraction</option>
                      <option value="activity">Activity</option>
                      <option value="rental">Rental</option>
                    </select>

                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Status
                    </label>
                    <select
                      value={status}
                      onChange={(e) => {
                        setStatus(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm text-gray-800 focus:outline-none focus:border-primary"
                    >
                      <option value="">All</option>
                      <option value="completed">Completed</option>
                      <option value="pending">Pending</option>
                      <option value="failed">Failed</option>
                      <option value="refunded">Refunded</option>
                    </select>

                    <label className="block text-xs font-medium text-gray-600 mb-1 mt-3">
                      Supplier
                    </label>
                    <select
                      value={supplierId}
                      onChange={(e) => {
                        setSupplierId(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm text-gray-800 focus:outline-none focus:border-primary"
                    >
                      <option value="">All suppliers</option>
                      {supplierOptions.map((s) => (
                        <option key={s.id} value={String(s.id)}>
                          {s.name}
                        </option>
                      ))}
                    </select>

                    {(serviceType || status || activeSearch || supplierId) && (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="mt-3 w-full text-primary hover:text-primary/80 text-sm"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </AdminPageCardToolbar>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3">
              <svg
                className="animate-spin h-8 w-8 text-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="text-primary font-medium">
                Loading transactions...
              </span>
            </div>
          </div>
        ) : (
          <AdminTable
            className="shadow-none rounded-none"
            footer={
              <AdminTableFooter>
                <Pagination pagination={pagination} onPageChange={handlePageChange} />
              </AdminTableFooter>
            }
          >
          <AdminTableHead>
            <AdminTableTh>#</AdminTableTh>
            <AdminTableTh>Service</AdminTableTh>
            <AdminTableTh>Customer</AdminTableTh>
            <AdminTableTh>Supplier</AdminTableTh>
            <AdminTableTh>Order ID</AdminTableTh>
            <AdminTableTh>Payment</AdminTableTh>
            <AdminTableTh>Amount</AdminTableTh>
            <AdminTableTh>Status</AdminTableTh>
            <AdminTableTh>Created On</AdminTableTh>
          </AdminTableHead>
          <AdminTableBody>
            {transactions.length === 0 ? (
              <TableEmptyRow
                colSpan={9}
                icon="fi fi-rr-receipt"
                emptyMessage="No transactions found"
                searchQuery={activeSearch || status || serviceType || supplierId}
                onClearSearch={clearFilters}
              />
            ) : (
              transactions.map((t, index) => (
                <AdminTableRow key={`${t.service_type}-${t.id}`}>
                  <AdminTableCell>
                    {(current_page - 1) * per_page + index + 1}
                  </AdminTableCell>
                  <AdminTableCell>
                    <div>
                      <p className="font-medium text-gray-900">{t.service_name || "—"}</p>
                      <p className="text-xs text-gray-500">
                        {formatServiceType(t.service_type)}
                        {t.payment_type ? ` • ${t.payment_type}` : ""}
                      </p>
                    </div>
                  </AdminTableCell>
                  <AdminTableCell>{t.customer_name || "—"}</AdminTableCell>
                  <AdminTableCell>{t.supplier_name || "—"}</AdminTableCell>
                  <AdminTableCell nowrap className="text-xs text-gray-700">
                    {t.order_id || "—"}
                  </AdminTableCell>
                  <AdminTableCell nowrap className="text-xs text-gray-700">
                    <div>
                      <p className="text-gray-900">{t.payment_method || "—"}</p>
                      <p className="text-gray-500">{t.payment_id || ""}</p>
                    </div>
                  </AdminTableCell>
                  <AdminTableCell className="text-gray-900">
                    {formatAmount(t.amount)}
                  </AdminTableCell>
                  <AdminTableCell>{renderStatusBadge(t.status)}</AdminTableCell>
                  <AdminTableCell>
                    {formatListingDateTime(t.created_at)}
                  </AdminTableCell>
                </AdminTableRow>
              ))
            )}
          </AdminTableBody>
        </AdminTable>
        )}
      </AdminPageCard>

      {showToaster && (
        <Toster
          message={toasterMessage}
          type={toasterType}
          duration={3000}
          onClose={() => setShowToaster(false)}
        />
      )}
    </AdminPageLayout>
  );
};

export default Transactions;

